import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import SEOHead from "../components/SEOHead";
import Reveal from "../components/Reveal";
import { clinic } from "../constants/clinic";

const Contact = () => {
  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "ご予約・お問い合わせ｜わかば接骨院",
    description: "わかば接骨院への電話予約とお問い合わせ方法。",
    url: `${clinic.domain}/contact`,
    mainEntity: {
      "@type": "MedicalClinic",
      name: clinic.name,
      telephone: clinic.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "共栄通3-5",
        addressLocality: "瀬戸市",
        addressRegion: "愛知県",
        postalCode: clinic.postalCode,
        addressCountry: "JP",
      },
    },
  };

  return (
    <>
      <SEOHead
        title="ご予約・お問い合わせ"
        description="瀬戸市のわかば接骨院へのご予約・ご相談は0561-83-3833へ。午前9時から12時、午後4時から8時までお電話で承ります。"
        keywords="わかば接骨院 予約,瀬戸市 接骨院 電話,瀬戸市 接骨院 問い合わせ"
        canonical="/contact"
        structuredData={contactStructuredData}
      />

      <Box component="section" sx={{ bgcolor: "#E8F0E8", py: { xs: 7, md: 10 }, position: "relative", overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Reveal>
            <Chip label="CONTACT" sx={{ mb: 2, bgcolor: "white", color: "primary.dark", fontWeight: 700, letterSpacing: ".12em" }} />
            <Typography component="h1" variant="h1" sx={{ fontSize: { xs: "2.3rem", md: "3.5rem" }, mb: 1.5 }}>
              ご予約・お問い合わせ
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { sm: "1.08rem" } }}>
              ご予約・ご相談は、お電話でお気軽にご連絡ください。
            </Typography>
          </Reveal>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr .9fr" }, gap: { xs: 4, md: 6 }, alignItems: "stretch" }}>
          <Reveal>
            <Paper elevation={0} sx={{ p: { xs: 4, sm: 5.5, md: 6 }, bgcolor: "primary.light", border: "1px solid", borderColor: "divider", color: "text.primary", height: "100%", position: "relative", overflow: "hidden" }}>
              <LocalPhoneRoundedIcon aria-hidden="true" sx={{ position: "absolute", right: -35, bottom: -60, fontSize: 250, opacity: .05 }} />
              <Typography sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: ".09em", mb: 1.5 }}>お電話でのご予約</Typography>
              <Typography variant="h3" sx={{ color: "text.primary", fontSize: { xs: "1.8rem", sm: "2.2rem" }, mb: 2 }}>
                お困りのことがあれば、<br />まずはお電話ください。
              </Typography>
              <Typography sx={{ color: "rgba(41,37,36,.78)", mb: 3.5 }}>
                ご希望の日時を伺い、ご予約をお取りします。分からないことも気軽にお尋ねください。
              </Typography>
              <Button component="a" href={clinic.phoneHref} variant="contained" size="large" startIcon={<LocalPhoneRoundedIcon />} sx={{ fontSize: { xs: "1.12rem", sm: "1.35rem" }, minHeight: 64, bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" } }}>
                {clinic.phone}
              </Button>
              <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mt: 3.5 }}>
                <AccessTimeRoundedIcon sx={{ color: "primary.dark", mt: .35 }} />
                <Typography variant="body2" sx={{ color: "rgba(41,37,36,.78)" }}>
                  午前9時から12時 / 午後4時から8時<br />
                  水曜・土曜は午前のみ　日曜・祝日は休診
                </Typography>
              </Stack>
            </Paper>
          </Reveal>

          <Reveal delay={100}>
            <Paper elevation={0} sx={{ p: { xs: 4, sm: 5.5 }, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <Typography variant="h4" sx={{ mb: 3 }}>ご予約の流れ</Typography>
              <Stack spacing={3}>
                {[
                  ["1", "お電話ください", `${clinic.phone} までご連絡ください。`],
                  ["2", "ご希望の日時をお伝えください", "診療時間内で空いている時間をご案内します。"],
                  ["3", "ご予約完了", "日時をご確認いただき、当日ご来院ください。"],
                ].map(([number, title, description]) => (
                  <Stack key={number} direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ width: 38, height: 38, borderRadius: "50%", bgcolor: "primary.light", color: "primary.dark", display: "grid", placeItems: "center", fontWeight: 700, flexShrink: 0 }}>
                      {number}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ mb: .25 }}>{title}</Typography>
                      <Typography variant="body2" color="text.secondary">{description}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Reveal>
        </Box>
      </Container>

      <Box component="section" sx={{ bgcolor: "#F5F5F4", py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <Reveal sx={{ textAlign: "center", mb: 5 }}>
            <Typography component="h2" variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.7rem" }, mb: 1.5 }}>
              初めてご来院になる方へ
            </Typography>
            <Typography color="text.secondary">安心してお越しいただけるよう、事前にご確認ください。</Typography>
          </Reveal>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
            {[
              ["マイナ保険証など", "保険施術をご希望の方は、マイナ保険証または資格確認書をお持ちください。"],
              ["動きやすい服装", "体の動きを確認しやすい、ゆったりとした服装がおすすめです。"],
              ["予約の変更", "変更やキャンセルの際は、分かり次第お電話でお知らせください。"],
            ].map(([title, text], index) => (
              <Reveal key={title} delay={index * 70}>
                <Paper elevation={0} sx={{ p: 3.5, border: "1px solid", borderColor: "divider", height: "100%" }}>
                  <CheckCircleRoundedIcon sx={{ color: "primary.dark", fontSize: 32, mb: 1.5 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Paper>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 } }}>
        <Reveal>
          <Paper elevation={0} sx={{ p: { xs: 4, sm: 5.5 }, border: "1px solid", borderColor: "divider" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={3}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <LocationOnRoundedIcon sx={{ color: "primary.dark", mt: .4 }} />
                <Box>
                  <Typography variant="h5" sx={{ mb: .7 }}>アクセス</Typography>
                  <Typography color="text.secondary">
                    〒{clinic.postalCode} {clinic.address}<br />
                    {clinic.station} / 専用駐車場9台
                  </Typography>
                </Box>
              </Stack>
              <Button component={RouterLink} to="/access" variant="outlined" endIcon={<ArrowForwardRoundedIcon />} sx={{ flexShrink: 0 }}>
                地図を見る
              </Button>
            </Stack>
          </Paper>
        </Reveal>
      </Container>
    </>
  );
};

export default Contact;
